// @flow
import { deleteSavedSearch, getSaveSearchState, submitSaveSearch } from '../../redux/modules/saveSearch';
import { fetchSavedSearchesIfNeeded, getSavedSearchById } from '../../redux/modules/savedSearch';
import { getCategoryPageUrl, getCombinedCategoryPageUrl, urlSafeTitle } from '../../utils/urls';
import {
    getSearchUiState,
    getSoldItemImportantCategories,
    getSoldItemImportantCreators,
    getSoldItemImportantOrigins,
    getSoldItemImportantStylePeriod,
} from '../../redux/modules/search';
import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { type CategoryLink } from '../../types/CategoryLink';
import { connect } from 'react-redux';
import { fetchItemsIfNeeded } from '../../redux/modules/item';
import { FormattedMessage } from 'react-intl';
import { getFetchFacetsSubmitted } from '../../redux/modules/searchFacets';
import { getItemCardDatas } from '../../redux/modules/cardData';
import { getSaveSearchCheckState } from '../../redux/modules/saveSearchCheck';
import { getSecondarySearchSuggestions } from '../../redux/modules/searchSuggestions';
import { getWindowSize } from '../../redux/modules/windowSize';
import { isUserLoggedIn } from '../../redux/modules/user';
import type { ItemCard } from '../../types/ItemCard';
import { type ItemFacets } from '../../types/ItemFacets';
import { trackZeroSearchResultsShelfClick } from '../../redux/modules/analytics';
import ZeroResults from '../../components/ZeroResults/ZeroResults';

const safeNameUrl = (unsafeName?: string = '') => {
    const cleanedName = unsafeName.toLowerCase().replace('&', '');
    return urlSafeTitle(cleanedName);
};

const mapStateToProps = state => {
    const saveSearchCheckState = getSaveSearchCheckState(state);
    const saveSearchState = getSaveSearchState(state);
    const { soldItemIds = [], submitted, totalSoldItems: soldItemsFound } = getSearchUiState(state);
    const soldItems = getItemCardDatas(state, soldItemIds.slice(0, 3)) || [];
    const { houses, suggestions } = getSecondarySearchSuggestions(state);

    const categoryLinks = [];
    const creators = getSoldItemImportantCreators(state);
    const categories = getSoldItemImportantCategories(state);
    const origins = getSoldItemImportantOrigins(state);
    const stylesPeriods = getSoldItemImportantStylePeriod(state);
    let savedSearch;
    if (saveSearchCheckState?.savedSearchId) {
        savedSearch = getSavedSearchById(state, saveSearchCheckState?.savedSearchId);
    }

    const searches = [];

    creators.forEach(obj => {
        const { id, name: unsafeName } = obj;
        const name = urlSafeTitle(unsafeName);
        categoryLinks.push({
            key: name,
            link: getCategoryPageUrl(name, id),
            title: unsafeName,
        });
    });

    const L1 = categories.find(cat => cat.level === 1);
    const style = stylesPeriods[0];
    if (L1 && style) {
        searches.push([Number(L1.id), Number(style.id)]);
        const link = getCombinedCategoryPageUrl(L1, style);
        const title = `${style.name} ${L1.name}`;
        categoryLinks.push({
            key: urlSafeTitle(title),
            link,
            title,
        });
    }

    const origin = origins[0];
    if (L1 && origin) {
        const link = getCombinedCategoryPageUrl(L1, origin);
        const title = `${origin.name} ${L1.name}`;
        categoryLinks.push({
            key: urlSafeTitle(title),
            link,
            title,
        });
    }

    categories
        .filter(cat => cat.level > 1)
        .forEach(obj => {
            const { id, name: unsafeName = '' } = obj;
            const name = safeNameUrl(unsafeName);
            categoryLinks.push({
                key: name,
                link: getCategoryPageUrl(name, id),
                title: unsafeName,
            });
        });

    const searchSubmitted = submitted || getFetchFacetsSubmitted(state);
    const isLoggedIn = isUserLoggedIn(state);
    const { width } = getWindowSize(state);

    return {
        categoryLinks,
        houses,
        isLoggedIn,
        savedSearch,
        saveSearchCheckState,
        saveSearchState,
        searchSubmitted,
        soldItems,
        soldItemsFound,
        submitSaveSearch,
        suggestions,
        width,
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            deleteSavedSearch,
            fetchItemsIfNeeded,
            fetchSavedSearchesIfNeeded,
            submitSaveSearch,
            trackZeroSearchResultsShelfClick,
        },
        dispatch
    );

type Props = {
    categoryLinks: CategoryLink[],
    categoryPage?: boolean,
    className: string,
    creator?: any,
    deleteSavedSearch: Function,
    facets: ItemFacets,
    fetchItemsIfNeeded: Function,
    fetchSavedSearchesIfNeeded: Function,
    houses: any[],
    isLoggedIn: boolean,
    savedSearch: any,
    saveSearchCheckState: any,
    saveSearchState: any,
    searchSubmitted: boolean,
    searchTerm: string,
    seeResultsRoute?: string,
    soldItems?: ItemCard[],
    soldItemsFound?: number,
    submitSaveSearch: Function,
    suggestions: any[],
    width: number,
};

class ZeroResultsContainer extends Component<Props> {
    static defaultProps = {
        searchTerm: '',
    };

    componentDidMount() {
        const { isLoggedIn } = this.props;

        if (isLoggedIn) {
            this.props.fetchSavedSearchesIfNeeded();
        }
    }

    componentDidUpdate(prevProps: Props) {
        const { isLoggedIn } = this.props;

        if (!prevProps.isLoggedIn && isLoggedIn) {
            this.props.fetchSavedSearchesIfNeeded();
        }
    }

    unSaveSearch = () => {
        const { savedSearch } = this.props;
        this.props.deleteSavedSearch(savedSearch);
    };

    render() {
        const {
            categoryLinks,
            categoryPage,
            className,
            creator,
            houses,
            isLoggedIn,
            saveSearchCheckState,
            saveSearchState,
            searchSubmitted,
            searchTerm,
            seeResultsRoute,
            soldItems,
            soldItemsFound,
            submitSaveSearch,
            suggestions,
            width,
        } = this.props;

        const tooltipMessage = <FormattedMessage id="follow_search_tooltip" values={{ token: null }} />;

        return (
            <ZeroResults
                categoryLinks={categoryLinks}
                categoryPage={categoryPage}
                className={className}
                creator={creator}
                deleteSavedSearch={this.unSaveSearch}
                fetchItemsIfNeeded={this.props.fetchItemsIfNeeded}
                houses={houses}
                isLoggedIn={isLoggedIn}
                saveSearchCheckState={saveSearchCheckState}
                saveSearchState={saveSearchState}
                searchSubmitted={searchSubmitted}
                searchTerm={searchTerm}
                seeResultsRoute={seeResultsRoute}
                soldItems={soldItems}
                soldItemsFound={soldItemsFound}
                submitSaveSearch={submitSaveSearch}
                suggestions={suggestions}
                tooltipMessage={tooltipMessage}
                trackZeroSearchResultsShelfClick={trackZeroSearchResultsShelfClick}
                width={width}
            />
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ZeroResultsContainer);
